import React, { useRef, FC, memo } from 'react';
import dynamic from 'next/dynamic';
import Debug from 'debug';

import { useRedirectIfNeeded } from '~/app/lib/router2/redirectIfNeeded/useRedirectIfNeeded';
import useFetchSessionUser from '~/app/lib/hooks/useFetchSessionUser';
import { OrchardBrands } from '~/app/lib/songwhipApi/types';
import { withEmitter } from '~/app/lib/hooks/useEmitter';
import { useI18n } from '~/app/lib/i18n';

import useCustomTracking from './hooks/useCustomTracking';
import useOnCopyGesture from './hooks/useOnCopyGesture';
import useCopyShareUrl from './hooks/useCopyShareUrl';
import { ItemPageLayoutProps } from './layouts/types';
import ItemPageGate from './components/ItemPageGate';
import { ItemPageContext } from './ItemPageContext';
import { PageThemeProvider } from './hooks/theme';
import PageLoading from '../PageLoading';
import ItemPageLayout from './layouts';
import { ItemContext } from './types';
import { toShareUrl } from './utils';
import PageError from '../PageError';

const debug = Debug('songwhip/components/ItemPage');

const OneTrustLazy = dynamic(() => import('../OneTrust'), { ssr: false });

export interface ItemPageProps
  extends Omit<ItemPageLayoutProps, 'settings' | 'contentRootRef'> {
  /**
   * Indicate whether there is enough content to render.
   *
   * If `false` and `isLoading: false` a loading spinner will
   * be shown. If `true` and `isLoading: true` then we can show
   * content and load in the background (eg. in refresh/localize case).
   *
   * This is also used internally infer decide whether to trigger
   * the 'page-view' tracking event. We don't want to trigger the
   * `page-view` if we haven't got data that needs to be sent with
   * the event.
   */
  hasContent: boolean;
  isLoading: boolean;
  isOwned: boolean | undefined;
  isDraft?: boolean;
  pageBrand?: OrchardBrands;
  pageOwnedByAccountIds: number[] | undefined;
  error?: Error;
  pagePath: string;
  itemContext: ItemContext;
  testId?: string;
  withRedirectIfNeeded?: boolean;
}

const ItemPage: FC<ItemPageProps> = ({
  withRedirectIfNeeded,
  pageOwnedByAccountIds,
  pagePath,
  isOwned,
  isDraft,
  pageBrand,
  itemContext,
  children,
  isLoading,
  hasContent,
  error,
  ...itemPageLayoutProps
}) => {
  debug('render');

  const contentRootRef = useRef<HTMLDivElement>(null);
  const shareUrl = toShareUrl({ isOwned, isDraft, pageBrand });
  const { isLoggedIn } = useFetchSessionUser();
  const { t } = useI18n('app');

  const copyShareUrl = useCopyShareUrl(shareUrl);

  useCustomTracking(itemContext);

  useOnCopyGesture({
    nodeRef: contentRootRef,
    callback: copyShareUrl,
  });

  if (withRedirectIfNeeded) {
    useRedirectIfNeeded({
      pagePath,
      pageOwnedByAccountIds,
    });
  }

  // only show loading spinner if the don't have any content to show
  // WARN: if we show the loading spinner when there *is* content on the page
  // we can end up wiping out the UI whenever redux regards the Item as being in
  // a "loading" state. This can end up closing dialog boxes etc. An example of
  // this is the EditDetailsDialog on the AlbumPage, we don't want to wipe out
  // the dialog box when the user is saving changes to the album.
  if (isLoading && !hasContent) {
    return <PageLoading />;
  }

  // only show the error if we don't have any useful content to show
  if (error && !hasContent) {
    return (
      <PageError
        error={error}
        toText={({ status }) => {
          if (status === 404) {
            return t('errors.item');
          }
        }}
      />
    );
  }

  return (
    <ItemPageGate>
      <ItemPageLayout
        contentRootRef={contentRootRef}
        settings={itemContext.layout.settings}
        {...itemPageLayoutProps}
      />
      {children}
      {!isLoggedIn && <OneTrustLazy sendConsentEvent={isOwned} />}
    </ItemPageGate>
  );
};

const ItemPageMemo = memo(
  withEmitter((props) => {
    const { itemContext } = props;

    return (
      <ItemPageContext.Provider value={itemContext}>
        <PageThemeProvider itemContext={itemContext}>
          <ItemPage {...props} />
        </PageThemeProvider>
      </ItemPageContext.Provider>
    );
  })
) as FC<ItemPageProps>;

export default ItemPageMemo;
